import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { DiscoverPost } from "@/mocks/discoverData";

const typeColorMap: Record<string, string> = {
  hotel: "#D97706",
  route: "#059669",
  car: "#2563EB",
  guide: "#7C3AED",
};

const typeIconMap: Record<string, string> = {
  hotel: "ri-hotel-bed-line",
  route: "ri-map-2-line",
  car: "ri-car-line",
  guide: "ri-user-star-line",
};

export default function PostCard({ post }: { post: DiscoverPost }) {
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likes);

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLiked(!liked);
    setLikeCount(liked ? likeCount - 1 : likeCount + 1);
  };

  const handleCard = () => {
    navigate(`/post-detail?id=${post.id}`);
  };

  const handleProduct = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (post.productLink) navigate(post.productLink.path);
  };

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden cursor-pointer transition-transform active:scale-[0.98]"
      onClick={handleCard}
    >
      {/* Cover Image */}
      <div className="relative overflow-hidden">
        <img
          src={post.cover}
          alt={post.title}
          className="w-full object-cover object-top"
          style={{ minHeight: 160, maxHeight: 260 }}
        />
        {post.productLink && (
          <div
            className="absolute bottom-2 left-2 right-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full cursor-pointer"
            style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)" }}
            onClick={handleProduct}
          >
            <i
              className={typeIconMap[post.productLink.type]}
              style={{ color: typeColorMap[post.productLink.type], fontSize: 12 }}
            />
            <span
              style={{ color: typeColorMap[post.productLink.type], fontSize: 11, fontWeight: 600 }}
              className="flex-1 truncate"
            >
              {post.productLink.label}
            </span>
            <i className="ri-arrow-right-s-line" style={{ color: "#9CA3AF", fontSize: 14, flexShrink: 0 }} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-2.5 pt-3">
        <div className="text-sm font-semibold text-gray-800 leading-snug mb-2 line-clamp-2">
          {post.title}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {post.tags.map((tag) => (
            <span
              key={tag}
              className="text-gray-400 rounded-full px-1.5 py-0.5"
              style={{ background: "#F5F5F5", fontSize: 10 }}
            >
              #{tag}
            </span>
          ))}
        </div>

        {/* 用户信息 + 互动 */}
        <div className="flex items-center justify-between">
          {/* 头像 + 姓名（姓名在头像上） */}
          <div className="flex items-center gap-2">
            <div className="relative flex-shrink-0 mt-3">
              <img
                src={post.avatar}
                alt={post.username}
                className="w-7 h-7 rounded-full object-cover object-top"
              />
              {/* 姓名在头像上方 */}
              <div
                className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap px-1.5 py-0.5 rounded-full text-white font-semibold"
                style={{ background: "rgba(0,0,0,0.55)", fontSize: 9, lineHeight: 1.4 }}
              >
                {post.username}
              </div>
            </div>
          </div>

          {/* 互动 */}
          <div className="flex items-center gap-2.5">
            <button
              className="flex items-center gap-1 cursor-pointer"
              onClick={handleLike}
            >
              <i
                className={liked ? "ri-heart-fill" : "ri-heart-line"}
                style={{ color: liked ? "#EF4444" : "#C4C8D0", fontSize: 14 }}
              />
              <span className="text-xs" style={{ color: "#B0B4BD" }}>
                {likeCount >= 1000 ? (likeCount / 1000).toFixed(1) + "k" : likeCount}
              </span>
            </button>
            <div className="flex items-center gap-1">
              <i className="ri-chat-1-line" style={{ color: "#C4C8D0", fontSize: 14 }} />
              <span className="text-xs" style={{ color: "#B0B4BD" }}>{post.comments}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
